import { z } from 'zod'
import {
  PROFILE_PHOTOS_MAX,
  PROFILE_RING_COLORS,
  PROFILE_STATUS_MAX_LENGTH,
} from '../validation/profile'
import { sharedPlanDtoSchema } from './dm'

/** A status line, shown to friends until `expiresAt`. */
export const profileStatusSchema = z.object({
  text: z.string(),
  setAt: z.string(),
  expiresAt: z.string(),
})

export const setStatusRequestSchema = z.object({
  /** One line; line breaks become spaces and the result is trimmed. */
  text: z
    .string()
    .min(1)
    .max(PROFILE_STATUS_MAX_LENGTH * 2),
})

/** An upcoming event someone is interested in, as a row on a profile. */
export const profileEventSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  locationName: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  /** "Bin dabei" on an RSVP event. */
  going: z.boolean(),
  /** Saved for oneself ("Nur für mich"); only ever in your own list. */
  onlyMe: z.boolean(),
  /** The viewer already sent this friend an invite to the event that is still open. */
  invited: z.boolean(),
})

export const profileEventsPageSchema = z.object({
  items: z.array(profileEventSchema),
  nextCursor: z.string().nullable(),
})

/** A photo shown on a profile besides the avatar. */
export const profilePhotoSchema = z.object({
  id: z.string().uuid(),
  url: z.string(),
})

/** Your own extra photos, in the order others see them. */
export const profilePhotosResponseSchema = z.object({
  photos: z.array(profilePhotoSchema),
})

/** Every one of your photo ids, in the new order. */
export const reorderProfilePhotosRequestSchema = z.object({
  ids: z.array(z.string().uuid()).max(PROFILE_PHOTOS_MAX),
})

export const profileRelationshipSchema = z.enum([
  'self',
  'friend',
  'pending_sent',
  'pending_received',
  'none',
])

/**
 * One person's profile as the viewer may see it. Status, age, interests, shared plans and
 * the 1:1 chat are filled for friends (and yourself) only; everyone else sees who the person is.
 */
export const profileViewSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  homeTown: z.string(),
  /** Links the person shows on their profile. */
  instagram: z.string().nullable(),
  linkedin: z.string().nullable(),
  /** "Über mich". */
  bio: z.string().nullable(),
  /** How the person wants to be referred to. */
  pronouns: z.string().nullable().optional(),
  /** The ring around the profile picture; null is automatic. */
  ringColor: z.enum(PROFILE_RING_COLORS).nullable().optional(),
  /** Extra photos after the avatar, in order. */
  photos: z.array(profilePhotoSchema),
  badge: z.string().nullable(),
  /** Joined on the web without an account, so there is no friend request to send. */
  isGuest: z.boolean(),
  relationship: profileRelationshipSchema,
  /** The friendship or open request between the two, to remove, withdraw or answer it. */
  friendshipId: z.string().uuid().nullable(),
  friendsSince: z.string().nullable(),
  mutualFriendCount: z.number().int(),
  directConversationId: z.string().uuid().nullable(),
  status: profileStatusSchema.nullable(),
  /** Age in years, when the person shares it with friends. */
  age: z.number().int().nullable(),
  /** Today (Vienna) is their birthday; only with a shared age. */
  birthdayToday: z.boolean(),
  interests: z.object({ items: z.array(profileEventSchema), total: z.number().int() }).nullable(),
  sharedPlans: z.array(sharedPlanDtoSchema),
})

export const commonFriendSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
})

export const commonEventSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().nullable(),
  title: z.string(),
  startTime: z.string(),
  imageUrl: z.string().nullable(),
})

export const commonPlanSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  /** Null for a plan without a date yet. */
  startTime: z.string().nullable(),
})

/** What the viewer and another person share: friends, upcoming events and plans. */
export const commonGroundSchema = z.object({
  mutualFriends: z.array(commonFriendSchema),
  mutualFriendCount: z.number().int(),
  sharedEvents: z.array(commonEventSchema),
  sharedPlans: z.array(commonPlanSchema),
})

/** Everyone you share with someone, and their other friends you may know (only for a friend). */
export const profilePeopleSchema = z.object({
  mutual: z.array(commonFriendSchema),
  mightKnow: z.array(commonFriendSchema.extend({ mutualFriendCount: z.number().int() })),
})

/** One bubble in the row above Chats. */
export const statusRowEntrySchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  /** Absent for a friend shown only because it is their birthday today. */
  status: profileStatusSchema.nullable(),
  birthdayToday: z.boolean(),
})

export const statusRowResponseSchema = z.object({
  mine: profileStatusSchema.nullable(),
  friends: z.array(statusRowEntrySchema),
})

export const sendEventInviteRequestSchema = z.object({
  eventId: z.string().uuid(),
})

export const sendEventInviteResponseSchema = z.object({
  conversationId: z.string().uuid(),
  messageId: z.string().uuid(),
})

export type ProfileStatus = z.infer<typeof profileStatusSchema>
export type ProfilePhoto = z.infer<typeof profilePhotoSchema>
export type ProfilePhotosResponse = z.infer<typeof profilePhotosResponseSchema>
export type CommonGround = z.infer<typeof commonGroundSchema>
export type ProfilePeople = z.infer<typeof profilePeopleSchema>
export type ProfileEvent = z.infer<typeof profileEventSchema>
export type ProfileEventsPage = z.infer<typeof profileEventsPageSchema>
export type ProfileRelationship = z.infer<typeof profileRelationshipSchema>
export type ProfileView = z.infer<typeof profileViewSchema>
export type StatusRowEntry = z.infer<typeof statusRowEntrySchema>
export type StatusRowResponse = z.infer<typeof statusRowResponseSchema>
